
import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLeadMetrics } from "@/hooks/useLeadMetrics";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FileBarChart2,
  Calendar,
  TrendingUp,
  Users,
  Target,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Mail,
  PhoneCall,
  Clock,
  Share2,
  Globe,
  ArrowRight,
  Trophy,
  Megaphone,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { Badge } from "@/components/ui/badge";
import { AdminReportNav } from "@/components/admin/AdminReportNav";
import { AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";

const Reports = () => {
  const [timeRange, setTimeRange] = useState("all");
  const navigate = useNavigate();
  const { metrics: serverMetrics, loading, error, refetch } = useLeadMetrics(timeRange);

  const stageData = useMemo(() => {
    const meta: Record<string, { name: string; color: string }> = {
      Νέο: { name: 'Νέα', color: '#3b82f6' },
      σεΕκκρεμότητα: { name: 'Εκκρεμότητα', color: '#f59e0b' },
      έκλεισεΗΔουλειά: { name: 'Επιτυχία', color: '#10b981' },
      ήθελεΠληροφορίες: { name: 'Πληροφορίες', color: '#6366f1' },
    };
    return serverMetrics.stage.map((row) => ({
      name: meta[row.name]?.name || row.name,
      value: row.total,
      color: meta[row.name]?.color || '#94a3b8',
    }));
  }, [serverMetrics.stage]);

  const serviceData = useMemo(() => {
    const meta: Record<string, { name: string; fill: string }> = {
      εξοικονομώ: { name: 'Εξοικονομώ', fill: '#3b82f6' },
      αντλίεςΘερμότητας: { name: 'Αντλίες', fill: '#8b5cf6' },
      φωτοβολταϊκά: { name: 'Φωτοβολταϊκά', fill: '#10b981' },
      κλιματιστικά: { name: 'Κλιματιστικά', fill: '#f59e0b' },
      φυσικόΑέριο: { name: 'Φυσικό Αέριο', fill: '#ef4444' },
      ηλιακά: { name: 'Ηλιακά', fill: '#14b8a6' },
      ανακαινίζωΕνεργειακά: { name: 'Ανακαινίζω · Ενεργειακά', fill: '#0ea5e9' },
      ανακαινίζωΠλήρηςΑνακαίνιση: { name: 'Ανακαινίζω · Ανακαίνιση', fill: '#e11d48' },
      άλλο: { name: 'Άλλο', fill: '#6b7280' },
    };
    return serverMetrics.service.map((row) => ({
      name: meta[row.name]?.name || row.name,
      value: row.total,
      fill: meta[row.name]?.fill || '#6b7280',
    })).sort((a, b) => b.value - a.value);
  }, [serverMetrics.service]);

  const sourceData = useMemo(() => {
    const total = serverMetrics.summary.total || 1;
    const meta: Record<string, { label: string; icon: LucideIcon; color: string; bg: string; bar: string }> = {
      facebook_api: { label: 'Meta API · Realtime', icon: Share2, color: 'text-violet-600', bg: 'bg-violet-50', bar: 'bg-violet-500' },
      facebook_leads: { label: 'Facebook Ads', icon: Share2, color: 'text-blue-600', bg: 'bg-blue-50', bar: 'bg-blue-500' },
      facebook_sheets: { label: 'Facebook Sheets', icon: Share2, color: 'text-indigo-600', bg: 'bg-indigo-50', bar: 'bg-indigo-500' },
      web: { label: 'Web Form', icon: Globe, color: 'text-slate-600', bg: 'bg-slate-50', bar: 'bg-slate-400' },
    };
    return serverMetrics.source.map((row) => ({
      ...(meta[row.name] || { label: row.name, icon: Globe, color: 'text-slate-600', bg: 'bg-slate-50', bar: 'bg-slate-400' }),
      count: row.total,
      pct: Math.round(row.total / total * 100),
    }));
  }, [serverMetrics.source, serverMetrics.summary.total]);

  const partnerRanking = useMemo(() => {
    return serverMetrics.partner
      .map(partner => ({
        id: partner.id,
        name: partner.name,
        leads: partner.total,
        success: partner.success,
        rate: partner.total > 0 ? Math.round((partner.success / partner.total) * 100) : 0,
      }))
      .filter(p => p.leads > 0)
      .sort((a, b) => b.success - a.success || b.rate - a.rate)
      .slice(0, 10);
  }, [serverMetrics.partner]);

  const campaignStats = useMemo(() => {
    return serverMetrics.campaign
      .map((row) => {
        const total = Number(row.total || 0);
        const success = Number(row.success || 0);
        return {
          name: row.name,
          total,
          success,
          pending: Number(row.pending || 0),
          newLeads: Number(row.new_leads || 0),
          rate: total > 0 ? Math.round((success / total) * 100) : 0,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [serverMetrics.campaign]);

  const metrics = useMemo(() => {
    const s = serverMetrics.summary;
    const total = s.total;
    const success = s.success;
    const pending = s.pending;
    const newLeads = s.new_leads;
    const unassigned = s.unassigned;
    const smsSent = s.sms_sent;
    const failedSms = s.sms_failed;
    const viewed = s.viewed;
    const todayLeads = s.today;
    const stalePending = s.stale_pending;
    return {
      total, success, pending, newLeads, unassigned, smsSent, failedSms, viewed, todayLeads, stalePending,
      successRate:    total > 0   ? Math.round((success / total) * 100) : 0,
      assignmentRate: total > 0   ? Math.round(((total - unassigned) / total) * 100) : 0,
      viewRate:       total > 0 ? Math.round((viewed / total) * 100) : 0,
    };
  }, [serverMetrics.summary]);

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Γενικές αναφορές" description="Φορτώνουμε τους βασικούς δείκτες της επιχείρησης." icon={<FileBarChart2 />} iconGradient="orange" eyebrow="Αναφορές επιχείρησης" />
        <AdminReportNav />
        <AdminMetricSkeletons />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Γενικές αναφορές" description="KPIs, pipeline, καμπάνιες και απόδοση δικτύου." icon={<FileBarChart2 />} iconGradient="orange" eyebrow="Αναφορές επιχείρησης" />
        <AdminReportNav />
        <AdminErrorState message={error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Γενικές αναφορές"
        description="KPIs επιχείρησης, pipeline, πηγές, καμπάνιες και απόδοση συνεργατών στην ίδια χρονική βάση."
        icon={<FileBarChart2 />}
        iconGradient="orange"
        eyebrow="Αναφορές επιχείρησης"
        actions={
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="h-11 w-full bg-white border-slate-200 sm:w-52">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Περίοδος" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλα τα δεδομένα</SelectItem>
              <SelectItem value="week">Τελευταία εβδομάδα</SelectItem>
              <SelectItem value="month">Τελευταίος μήνας</SelectItem>
              <SelectItem value="quarter">Τελευταίο τρίμηνο</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <AdminReportNav />

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard value={metrics.total}               title="Σύνολο Leads"    icon={<Target />}     variant="info"                                                     size="compact" />
        <StatCard value={`${metrics.successRate}%`}   title="Ποσοστό Επιτυχίας" icon={<TrendingUp />} variant="success"                                               size="compact" />
        <StatCard value={`${metrics.assignmentRate}%`}title="Κάλυψη Ανάθεσης" icon={<Users />}      variant="gradient" size="compact" />
        <StatCard value={metrics.todayLeads}          title="Σήμερα"          icon={<Clock />}      variant="warning"                                                  size="compact" />
      </div>

      {/* Alerts + Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Σημεία Προσοχής</CardTitle>
            <CardDescription>Απαιτούν άμεση ενέργεια — κλικ για προβολή</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={() => navigate("/admin/operations")}
              className={`rounded-xl border p-3 text-left transition-all ${
                metrics.unassigned > 0
                  ? "border-amber-200 bg-amber-50 hover:bg-amber-100 cursor-pointer"
                  : "border-slate-100 bg-slate-50 cursor-default"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-amber-700 font-medium">Χωρίς ανάθεση</p>
                {metrics.unassigned > 0 && <ArrowRight className="h-3 w-3 text-amber-500" />}
              </div>
              <p className="text-2xl font-bold text-amber-800">{metrics.unassigned}</p>
            </button>
            <button
              onClick={() => navigate("/admin/leads?queue=stale")}
              className={`rounded-xl border p-3 text-left transition-all ${
                metrics.stalePending > 0
                  ? "border-orange-200 bg-orange-50 hover:bg-orange-100 cursor-pointer"
                  : "border-slate-100 bg-slate-50 cursor-default"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-orange-700 font-medium">Εκκρεμότητα 48h+</p>
                {metrics.stalePending > 0 && <ArrowRight className="h-3 w-3 text-orange-500" />}
              </div>
              <p className="text-2xl font-bold text-orange-800">{metrics.stalePending}</p>
            </button>
            <button
              onClick={() => navigate("/admin/operations")}
              className={`rounded-xl border p-3 text-left transition-all ${
                metrics.failedSms > 0
                  ? "border-red-200 bg-red-50 hover:bg-red-100 cursor-pointer"
                  : "border-slate-100 bg-slate-50 cursor-default"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-red-700 font-medium">Αποτυχίες SMS</p>
                {metrics.failedSms > 0 && <ArrowRight className="h-3 w-3 text-red-500" />}
              </div>
              <p className="text-2xl font-bold text-red-800">{metrics.failedSms}</p>
            </button>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Γρήγορες Συνδέσεις</CardTitle>
            <CardDescription>Εργαλεία διαχείρισης</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: "/admin/leads",               label: "Λίστα Leads",    icon: Target,      color: "text-blue-600",    bg: "bg-blue-50" },
                { to: "/admin/leads/statistics",    label: "Στατιστικά",    icon: TrendingUp,  color: "text-emerald-600", bg: "bg-emerald-50" },
                { to: "/admin/messages/statistics", label: "SMS Stats",     icon: PhoneCall,   color: "text-violet-600",  bg: "bg-violet-50" },
                { to: "/admin/email/statistics",    label: "Email Stats",   icon: Mail,        color: "text-orange-600",  bg: "bg-orange-50" },
              ].map(({ to, label, icon: Icon, color, bg }) => (
                <Link key={to} to={to}>
                  <div className="group flex flex-col items-center gap-2 rounded-xl border border-slate-100 bg-white p-3 text-center hover:border-slate-200 hover:shadow-sm transition-all">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg} group-hover:scale-110 transition-transform`}>
                      <Icon className={`h-4 w-4 ${color}`} />
                    </div>
                    <p className="text-xs font-semibold text-slate-700 leading-tight">{label}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current pipeline distribution */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-50">
                <Target className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Κατανομή pipeline</CardTitle>
                <CardDescription>Τρέχουσα κατάσταση — όχι διαδοχικό conversion funnel</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {stageData.map((entry) => {
              const percentage = metrics.total > 0 ? Math.round((entry.value / metrics.total) * 100) : 0;
              return (
                <div key={entry.name}>
                  <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2 text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />{entry.name}</span>
                    <span className="font-semibold tabular-nums text-slate-900">{entry.value} · {percentage}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: entry.color }} /></div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Service demand */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <FileBarChart2 className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Ζήτηση ανά Υπηρεσία</CardTitle>
                <CardDescription>Ποια υπηρεσία φέρνει περισσότερα leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serviceData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                <Bar dataKey="value" name="Leads" radius={[0, 4, 4, 0]}>
                  {serviceData.map(entry => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Campaign conversion rate */}
      {campaignStats.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-violet-50">
                <Megaphone className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-base">Απόδοση Καμπανιών</CardTitle>
                <CardDescription>Ποσοστό μετατροπής ανά καμπάνια διαφήμισης</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 md:hidden">
              {campaignStats.map((campaign) => (
                <article key={campaign.name} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{campaign.name}</p>
                    <Badge variant="outline" className="shrink-0 bg-white">{campaign.rate}%</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><p className="font-bold tabular-nums text-slate-900">{campaign.total}</p><p className="text-[10px] uppercase text-slate-400">Leads</p></div>
                    <div><p className="font-bold tabular-nums text-emerald-700">{campaign.success}</p><p className="text-[10px] uppercase text-slate-400">Επιτυχίες</p></div>
                    <div><p className="font-bold tabular-nums text-amber-700">{campaign.pending}</p><p className="text-[10px] uppercase text-slate-400">Εκκρεμή</p></div>
                  </div>
                </article>
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left pb-2">Καμπάνια</th>
                    <th className="text-right pb-2">Leads</th>
                    <th className="text-right pb-2">Επιτυχίες</th>
                    <th className="text-right pb-2">Εκκρεμή</th>
                    <th className="text-right pb-2 w-40">Μετατροπή</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {campaignStats.map((c) => (
                    <tr key={c.name} className="hover:bg-slate-50 transition-colors">
                      <td className="py-2 pr-4">
                        <span className="flex items-center gap-1.5 font-medium text-slate-800 max-w-[300px] truncate">
                          <Megaphone className="h-3.5 w-3.5 text-violet-400 shrink-0" />
                          {c.name}
                        </span>
                      </td>
                      <td className="py-2 text-right text-slate-600 font-medium">{c.total}</td>
                      <td className="py-2 text-right">
                        <span className="font-semibold text-emerald-600">{c.success}</span>
                      </td>
                      <td className="py-2 text-right">
                        <span className="text-amber-600">{c.pending}</span>
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${c.rate}%`,
                                backgroundColor: c.rate >= 40 ? "#10b981" : c.rate >= 20 ? "#f59e0b" : "#94a3b8",
                              }}
                            />
                          </div>
                          <span className={`text-xs font-bold w-9 text-right ${c.rate >= 40 ? "text-emerald-600" : c.rate >= 20 ? "text-amber-600" : "text-slate-500"}`}>
                            {c.rate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td className="py-2 text-xs font-bold text-slate-600 uppercase">Σύνολο</td>
                    <td className="py-2 text-right text-xs font-bold text-slate-700">{campaignStats.reduce((s, c) => s + c.total, 0)}</td>
                    <td className="py-2 text-right text-xs font-bold text-emerald-600">{campaignStats.reduce((s, c) => s + c.success, 0)}</td>
                    <td className="py-2 text-right text-xs font-bold text-amber-600">{campaignStats.reduce((s, c) => s + c.pending, 0)}</td>
                    <td className="py-2 text-right text-xs font-bold text-slate-600">
                      {(() => {
                        const total = campaignStats.reduce((s, c) => s + c.total, 0);
                        const success = campaignStats.reduce((s, c) => s + c.success, 0);
                        return total > 0 ? `${Math.round((success / total) * 100)}%` : "0%";
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partner ranking + Source + Channel health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Partner ranking table */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-50">
                <Trophy className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base">Κατάταξη Συνεργατών</CardTitle>
                <CardDescription>Top 10 ανά επιτυχίες — με ποσοστό μετατροπής</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {partnerRanking.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Δεν υπάρχουν δεδομένα για την περίοδο</p>
            ) : (
              <>
              <div className="space-y-2 sm:hidden">
                {partnerRanking.map((partner, index) => (
                  <Link key={partner.id} to={`/admin/partners/${partner.id}/dashboard`} className="flex min-h-14 items-center gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-bold text-slate-500 ring-1 ring-slate-200">{index + 1}</span>
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-slate-900">{partner.name}</span><span className="text-xs text-slate-500">{partner.leads} leads · {partner.success} επιτυχίες</span></span>
                    <span className="text-sm font-bold tabular-nums text-emerald-700">{partner.rate}%</span>
                  </Link>
                ))}
              </div>
              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                      <th className="text-left pb-2 w-6">#</th>
                      <th className="text-left pb-2">Συνεργάτης</th>
                      <th className="text-right pb-2">Leads</th>
                      <th className="text-right pb-2">Επιτυχίες</th>
                      <th className="text-right pb-2">Ποσοστό</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {partnerRanking.map((p, i) => (
                      <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-2 pr-3">
                          <span className={`text-xs font-bold ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-orange-400" : "text-slate-300"}`}>
                            {i + 1}
                          </span>
                        </td>
                        <td className="py-2">
                          <Link to={`/admin/partners/${p.id}/dashboard`} className="font-medium text-slate-800 hover:text-blue-600 transition-colors truncate max-w-[180px] block">
                            {p.name}
                          </Link>
                        </td>
                        <td className="py-2 text-right text-slate-600">{p.leads}</td>
                        <td className="py-2 text-right">
                          <span className="font-semibold text-emerald-600">{p.success}</span>
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${p.rate}%` }} />
                            </div>
                            <span className={`text-xs font-bold w-9 text-right ${p.rate >= 50 ? "text-emerald-600" : p.rate >= 25 ? "text-amber-600" : "text-slate-500"}`}>
                              {p.rate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Right column: Source + Channel health */}
        <div className="space-y-4">
          {/* Source breakdown */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Πηγές Leads</CardTitle>
              <CardDescription>Ποια κανάλια φέρνουν επαφές</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {sourceData.map(s => (
                <div key={s.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`flex items-center gap-1.5 text-xs font-medium ${s.color}`}>
                      <s.icon className="h-3 w-3" />
                      {s.label}
                    </span>
                    <span className="text-xs text-slate-600 font-semibold">{s.count} <span className="text-slate-400 font-normal">({s.pct}%)</span></span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Channel health */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Υγεία Επικοινωνίας</CardTitle>
              <CardDescription>Αποστολή και άνοιγμα ασφαλούς lead link</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
                <span className="text-xs text-blue-700 font-medium flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  SMS Εστάλησαν
                </span>
                <span className="font-bold text-blue-800">{metrics.smsSent}</span>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-slate-600 font-medium">Leads που προβλήθηκαν</span>
                  <span className={`text-xs font-bold ${metrics.viewRate >= 50 ? "text-emerald-600" : metrics.viewRate >= 25 ? "text-amber-600" : "text-rose-600"}`}>
                    {metrics.viewRate}%
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${metrics.viewRate >= 50 ? "bg-emerald-500" : metrics.viewRate >= 25 ? "bg-amber-500" : "bg-rose-500"}`}
                    style={{ width: `${metrics.viewRate}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                <span className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Σε Εκκρεμότητα
                </span>
                <span className="font-bold text-amber-800">{metrics.pending}</span>
              </div>

              {metrics.failedSms > 0 && (
                <div className="flex items-center justify-between rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                  <span className="text-xs text-red-700 font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Αποτυχίες SMS
                  </span>
                  <span className="font-bold text-red-800">{metrics.failedSms}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Reports;
